// Privacy-first identity helper.
// Public surfaces should always show "@username". Real names are PII and
// MUST NOT be used as a fallback handle — when no username exists we surface
// a neutral, anonymous handle instead.
//
// Usage:
//   handle(obj, 'driver')                          -> "@username" or anon fallback
//   handle({ username, name })                      -> "@username" or anon fallback
//   handleWithFallback(driver_username, driver_name)  // `name` is unused (kept for API compat)

const FALLBACK = 'skater';

const stripAt = (s) => (typeof s === 'string' ? s.replace(/^@+/, '') : '');

// Stable anonymous handle when no username — uses a short hash of an opaque
// id (user_id, name token, etc.) so the SAME user always renders as the SAME
// "@skater-xxxx" rather than dropping to their real first name.
function anonHandle(seed) {
  const s = typeof seed === 'string' ? seed : '';
  if (!s) return `@${FALLBACK}`;
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  const tag = Math.abs(h).toString(36).slice(0, 4);
  return `@${FALLBACK}-${tag}`;
}

export function handleWithFallback(username /* , name (deprecated, ignored) */) {
  const u = stripAt(username);
  if (u) return `@${u}`;
  // No real-name fallback any more — neutral anon handle only.
  // (signature keeps `name` arg for backwards-compat with existing callers.)
  // eslint-disable-next-line prefer-rest-params
  const seed = arguments.length > 1 ? String(arguments[1] || '') : '';
  return anonHandle(seed);
}

// Convenience for objects like {driver_username, driver_name}
export function handle(obj, prefix) {
  if (!obj) return `@${FALLBACK}`;
  const u = prefix ? obj[`${prefix}_username`] : obj.username;
  // Seed the anonymous fallback with user_id (preferred) or a hashable token
  // so the handle stays stable across page renders without exposing the name.
  const seed = (prefix ? obj[`${prefix}_user_id`] : obj.user_id)
    || (prefix ? obj[`${prefix}_id`] : obj.id)
    || (prefix ? obj[`${prefix}_name`] : obj.name)
    || '';
  return handleWithFallback(u, seed);
}

// For places where we want JUST the visible token (no leading @) — e.g. for
// avatar fallback letters or aria-labels.
export function firstChar(obj, prefix) {
  const h = handle(obj, prefix);
  return (h.replace(/^@/, '')[0] || '?').toUpperCase();
}
